<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  resourceKey: { type: String, required: true },
  selectedIds: { type: Array, required: true },
  inboxes: { type: Array, required: true },
  busy: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['save']);
const { t } = useI18n();
const container = ref(null);
const trigger = ref(null);
const searchInput = ref(null);
const draft = ref([]);
const query = ref('');
const open = ref(false);
const locked = computed(() => props.disabled || props.busy);
const fieldId = computed(() => `resource-inboxes-${props.resourceKey}`);
const dirty = computed(
  () =>
    JSON.stringify([...draft.value].sort((a, b) => a - b)) !==
    JSON.stringify([...props.selectedIds].sort((a, b) => a - b))
);
const selected = computed(() =>
  draft.value.map(id => ({
    id,
    name:
      props.inboxes.find(inbox => inbox.id === id)?.name ||
      t('ACCOUNT_RESOURCES.INBOX_SCOPE.MISSING', { id }),
  }))
);
const visibleSelected = computed(() => selected.value.slice(0, 2));
const remaining = computed(() => Math.max(selected.value.length - 2, 0));
const normalize = value =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase();
const filteredInboxes = computed(() =>
  props.inboxes
    .filter(inbox =>
      normalize(inbox.name).includes(normalize(query.value.trim()))
    )
    .slice()
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
);
const close = (restoreFocus = false) => {
  open.value = false;
  if (restoreFocus) nextTick(() => trigger.value?.focus());
};
const toggle = async () => {
  if (locked.value) return;
  if (open.value) {
    close();
    return;
  }
  query.value = '';
  open.value = true;
  await nextTick();
  searchInput.value?.focus();
};
const remove = id => {
  if (!locked.value) draft.value = draft.value.filter(value => value !== id);
};
const toggleInbox = (id, checked) => {
  if (locked.value) return;
  if (checked && !draft.value.includes(id)) draft.value = [...draft.value, id];
  if (!checked) remove(id);
};
const reset = () => {
  draft.value = [...props.selectedIds];
  query.value = '';
  close();
};
const save = () => {
  if (locked.value || !dirty.value) return;
  close();
  emit('save', [...draft.value]);
};
onClickOutside(container, () => close());
watch(() => props.selectedIds, reset, { immediate: true });
watch(locked, value => {
  if (value) close();
});
</script>

<template>
  <div class="mt-3 flex min-w-0 flex-col gap-2 border-t border-n-weak pt-3">
    <label
      :id="`${fieldId}-label`"
      :for="fieldId"
      class="m-0 text-sm font-medium text-n-slate-12"
    >
      {{ t('ACCOUNT_RESOURCES.INBOX_SCOPE.LABEL') }}
    </label>
    <div
      ref="container"
      class="relative min-w-0"
      @keydown.esc.stop.prevent="close(true)"
    >
      <div
        class="flex min-h-10 min-w-0 flex-wrap items-center gap-1 rounded-xl border border-n-weak bg-n-surface-1 p-1.5 focus-within:ring-1 focus-within:ring-n-brand"
        :class="{ 'opacity-50': locked }"
        @click="toggle"
      >
        <span
          v-for="inbox in visibleSelected"
          :key="inbox.id"
          class="inline-flex min-w-0 max-w-full items-center gap-1 rounded-md bg-n-surface-2 py-1 ps-2 pe-1 text-xs text-n-slate-12"
        >
          <span class="min-w-0 max-w-40 truncate" :title="inbox.name">{{
            inbox.name
          }}</span>
          <button
            type="button"
            class="inline-flex size-4 shrink-0 items-center justify-center rounded text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-not-allowed"
            :aria-label="
              t('ACCOUNT_RESOURCES.INBOX_SCOPE.REMOVE', { name: inbox.name })
            "
            :disabled="locked"
            @click.stop="remove(inbox.id)"
          >
            <Icon icon="i-lucide-x" class="size-3" aria-hidden="true" />
          </button>
        </span>
        <button
          :id="fieldId"
          ref="trigger"
          type="button"
          class="flex min-h-6 min-w-8 flex-1 items-center gap-2 rounded-md px-1 text-start text-xs text-n-slate-11 focus:outline-none disabled:cursor-not-allowed"
          :aria-labelledby="`${fieldId}-label`"
          :aria-expanded="open"
          :aria-controls="`${fieldId}-options`"
          :disabled="locked"
          @click.stop="toggle"
        >
          <span v-if="!selected.length" class="min-w-0 flex-1 truncate">{{
            t('ACCOUNT_RESOURCES.INBOX_SCOPE.EMPTY')
          }}</span>
          <span
            v-else-if="remaining"
            :aria-label="
              t('ACCOUNT_RESOURCES.INBOX_SCOPE.MORE', { count: remaining })
            "
            >+{{ remaining }}</span
          >
          <Icon
            :icon="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
            class="ms-auto size-4 shrink-0"
            aria-hidden="true"
          />
        </button>
      </div>
      <div
        v-if="open"
        :id="`${fieldId}-options`"
        role="group"
        :aria-labelledby="`${fieldId}-label`"
        class="absolute start-0 top-full z-30 mt-2 w-full min-w-0 rounded-xl border border-n-weak bg-n-surface-1 p-2 shadow-lg"
      >
        <div class="relative mb-2">
          <Icon
            icon="i-lucide-search"
            class="pointer-events-none absolute start-3 top-1/2 size-3.5 -translate-y-1/2 text-n-slate-10"
            aria-hidden="true"
          />
          <input
            ref="searchInput"
            v-model="query"
            type="search"
            autocomplete="off"
            :placeholder="t('ACCOUNT_RESOURCES.INBOX_SCOPE.SEARCH')"
            :aria-label="t('ACCOUNT_RESOURCES.INBOX_SCOPE.SEARCH')"
            class="!m-0 !h-9 !rounded-lg !border-n-weak !bg-n-surface-1 !ps-9 !pe-3 !text-sm !text-n-slate-12"
          />
        </div>
        <div class="max-h-56 overflow-y-auto overscroll-contain">
          <label
            v-for="inbox in filteredInboxes"
            :key="inbox.id"
            class="!m-0 flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-normal text-n-slate-12 hover:bg-n-surface-2 focus-within:bg-n-surface-2"
          >
            <Checkbox
              class="shrink-0"
              :model-value="draft.includes(inbox.id)"
              :disabled="locked"
              @change="toggleInbox(inbox.id, $event.target.checked)"
            />
            <span class="min-w-0 break-words">{{ inbox.name }}</span>
          </label>
          <p
            v-if="!filteredInboxes.length"
            class="m-0 px-2 py-3 text-sm text-n-slate-11"
          >
            {{ t('ACCOUNT_RESOURCES.INBOX_SCOPE.NO_RESULTS') }}
          </p>
        </div>
      </div>
    </div>
    <p class="m-0 text-xs text-n-slate-11">
      {{ t('ACCOUNT_RESOURCES.INBOX_SCOPE.HINT') }}
    </p>
    <div v-if="dirty" class="flex flex-wrap gap-2">
      <Button
        type="button"
        :label="t('ACCOUNT_RESOURCES.INBOX_SCOPE.SAVE')"
        :disabled="locked"
        :is-loading="busy"
        size="sm"
        @click="save"
      />
      <Button
        type="button"
        :label="t('ACCOUNT_RESOURCES.INBOX_SCOPE.CANCEL')"
        :disabled="locked"
        size="sm"
        color="slate"
        variant="ghost"
        @click="reset"
      />
    </div>
  </div>
</template>
